import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { loginThunk, resendConfirmationThunk } from '../redux/userSlice'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useCollaborations } from '../hooks/useCollaborations'
import { Input } from '../components/ui/input'
import { Button } from '../components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs'
import { AppDispatch, RootState } from '../store'
import api from '../lib/api'

const useAppDispatch = () => useDispatch<AppDispatch>()

const SignIn: React.FC = () => {
  // Pre-fill credentials in development mode for easier testing
  const [signInEmail, setSignInEmail] = useState(import.meta.env.DEV ? 'sarah.chen@psu.edu' : '')
  const [signInPassword, setSignInPassword] = useState(import.meta.env.DEV ? 'athlete123' : '')
  const [signUpEmail, setSignUpEmail] = useState('')
  const [signUpPassword, setSignUpPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [signUpStep, setSignUpStep] = useState<1 | 2>(1)
  const [checkingEmail, setCheckingEmail] = useState(false)
  const [activeTab, setActiveTab] = useState<'signup' | 'signin'>('signup')
  const [error, setError] = useState<React.ReactNode>('')
  const [needsVerification, setNeedsVerification] = useState(false)

  // New state to toggle between magic link and password login
  const [showPasswordLogin, setShowPasswordLogin] = useState(false)
  // State for magic link success message
  const [magicLinkSent, setMagicLinkSent] = useState(false)

  const dispatch = useAppDispatch()
  const isLoading = useSelector((state: RootState) => state.user.loading)
  const navigate = useNavigate()
  const { athlete, requestMagicLink } = useAuth()
  const { refetch: refreshCampaigns } = useCollaborations()

  // If already signed in, redirect to home
  useEffect(() => {
    if (athlete) {
      navigate('/', { replace: true })
    }
  }, [athlete, navigate])

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    try {
      const result = await dispatch(
        loginThunk({ email: signInEmail, password: signInPassword }),
      ).unwrap()
      if (result) {
        refreshCampaigns()
        navigate('/')
      }
    } catch (err: unknown) {
      const error = err as { email_verified?: boolean; message?: string }
      if (error.email_verified === false) {
        setError(error.message || 'Please verify your email')
        setNeedsVerification(true)
      } else {
        setError(error.message || 'Login failed. Please try again.')
        setNeedsVerification(false)
      }
    }
  }

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!signInEmail) {
      setError('Please enter your email address.')
      return
    }

    try {
      await requestMagicLink(signInEmail)
      setMagicLinkSent(true)
    } catch (err: any) {
      setError(err.message || 'Failed to send magic link. Please try again.')
    }
  }

  const handleSignUpContinue = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setCheckingEmail(true)
    try {
      const { data } = await api.post('/users/check-email', { email: signUpEmail })
      if (data.exists) {
        setError(
          <span>
            An account with that email already exists.{' '}
            <button
              type="button"
              onClick={() => setActiveTab('signin')}
              className="text-[#C0FD72] hover:text-[#a8e45a] font-medium underline"
            >
              Sign in instead?
            </button>
          </span>,
        )
        return
      }
      setSignUpStep(2)
    } catch {
      setError('Failed to verify email. Please try again.')
    } finally {
      setCheckingEmail(false)
    }
  }

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (signUpPassword !== confirmPassword) {
      setError('Passwords do not match.')
      return
    }

    if (signUpPassword.length < 6) {
      setError('Password must be at least 6 characters long')
      return
    }

    try {
      // Store signup email and password for onboarding
      sessionStorage.setItem('signupEmail', signUpEmail)
      sessionStorage.setItem('signupPassword', signUpPassword)

      // Navigate to onboarding to complete profile
      navigate('/onboarding')
    } catch (err: unknown) {
      const error = err as { message?: string }
      setError(error.message || 'Sign up failed. Please try again.')
    }
  }

  const handleResendConfirmation = async () => {
    if (!signInEmail) {
      setError('Please enter your email address to resend the confirmation.')
      return
    }
    try {
      await dispatch(resendConfirmationThunk(signInEmail)).unwrap()
      setError('A new confirmation email has been sent. Please check your inbox.')
      setNeedsVerification(false)
    } catch (resendErr: unknown) {
      const error = resendErr as { message?: string }
      setError(error.message || 'Failed to resend confirmation email.')
    }
  }

  return (
    <div className="flex items-center justify-center min-h-screen relative">
      {/* Gradient background similar to the one in Layout.tsx */}
      <div className="fixed inset-0 -z-10 bg-linear-to-br from-[#000000] via-[#0a1e29] to-[#003b4d]"></div>

      <div className="w-full max-w-md px-4">
        <Card variant="dark">
          <CardHeader className="text-center pb-0">
            <div className="flex justify-center mb-6">
              <img
                src="https://adthlete.us-iad-1.linodeobjects.com/public/adthlete_logomark.png"
                alt="Athios Logo"
                className="w-10 h-10"
              />
            </div>
            <CardDescription className="mt-2 text-gray-400">
              Create or sign in to your Athios account
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            {error && <p className="text-red-500 mb-4 text-sm text-center">{error}</p>}
            {magicLinkSent && (
              <div className="bg-[#C0FD72]/10 border border-[#C0FD72]/20 rounded-md p-4 mb-6 text-center">
                <p className="text-[#C0FD72] font-medium">Magic link sent!</p>
                <p className="text-gray-300 text-sm mt-1">
                  Check your email for a link to sign in.
                </p>
              </div>
            )}

            <Tabs
              value={activeTab}
              onValueChange={(v) => {
                setActiveTab(v as 'signup' | 'signin')
                setError('')
              }}
              className="w-full"
            >
              <TabsList className="w-full grid grid-cols-2 mb-6">
                <TabsTrigger value="signup">Create Account</TabsTrigger>
                <TabsTrigger value="signin">Sign In</TabsTrigger>
              </TabsList>

              <TabsContent value="signup">
                {signUpStep === 1 ? (
                  <form onSubmit={handleSignUpContinue} className="space-y-4">
                    <div>
                      <Input
                        type="email"
                        placeholder="Email"
                        value={signUpEmail}
                        onChange={(e) => setSignUpEmail(e.target.value)}
                        className="bg-white/5 text-white border-white/10 focus:border-[#C0FD72] focus:ring-[#C0FD72]/20"
                        required
                      />
                    </div>
                    <Button
                      type="submit"
                      variant="lime"
                      className="w-full font-medium"
                      disabled={checkingEmail}
                    >
                      {checkingEmail ? 'Checking...' : 'Continue'}
                    </Button>
                  </form>
                ) : (
                  <form onSubmit={handleSignUp} className="space-y-4">
                    <div>
                      <Input
                        type="email"
                        value={signUpEmail}
                        className="bg-white/5 text-white border-white/10 opacity-60 cursor-not-allowed"
                        disabled
                      />
                    </div>
                    <div>
                      <Input
                        type="password"
                        placeholder="Password"
                        value={signUpPassword}
                        onChange={(e) => setSignUpPassword(e.target.value)}
                        className="bg-white/5 text-white border-white/10 focus:border-[#C0FD72] focus:ring-[#C0FD72]/20"
                        required
                      />
                    </div>
                    <div>
                      <Input
                        type="password"
                        placeholder="Confirm Password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="bg-white/5 text-white border-white/10 focus:border-[#C0FD72] focus:ring-[#C0FD72]/20"
                        required
                      />
                    </div>
                    <Button
                      type="submit"
                      variant="lime"
                      className="w-full font-medium"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Creating Account...' : 'Sign Up'}
                    </Button>
                    <div className="text-center">
                      <button
                        type="button"
                        onClick={() => {
                          setSignUpStep(1)
                          setError('')
                        }}
                        className="text-gray-400 hover:text-white text-sm underline underline-offset-4"
                      >
                        Use a different email
                      </button>
                    </div>
                  </form>
                )}
              </TabsContent>

              <TabsContent value="signin">
                {showPasswordLogin ? (
                  /* Password Login View */
                  <form onSubmit={handleSignIn} className="space-y-4">
                    <div>
                      <Input
                        type="email"
                        placeholder="Email"
                        value={signInEmail}
                        onChange={(e) => setSignInEmail(e.target.value)}
                        className="bg-white/5 text-white border-white/10 focus:border-[#C0FD72] focus:ring-[#C0FD72]/20"
                        required
                      />
                    </div>
                    <div>
                      <Input
                        type="password"
                        placeholder="Password"
                        value={signInPassword}
                        onChange={(e) => setSignInPassword(e.target.value)}
                        className="bg-white/5 text-white border-white/10 focus:border-[#C0FD72] focus:ring-[#C0FD72]/20"
                        required
                      />
                    </div>
                    <Button
                      type="submit"
                      variant="lime"
                      className="w-full font-medium"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Signing In...' : 'Sign In'}
                    </Button>

                    <div className="mt-4 text-center space-y-2">
                      <Link
                        to="/forgot-password"
                        className="block text-[#C0FD72] hover:text-[#a8e45a] font-medium text-sm"
                      >
                        Forgot your password?
                      </Link>
                      <button
                        type="button"
                        onClick={() => setShowPasswordLogin(false)}
                        className="text-gray-400 hover:text-white text-sm underline underline-offset-4"
                      >
                        Back to magic link sign in
                      </button>
                    </div>
                  </form>
                ) : (
                  /* Magic Link View (Default) */
                  <form onSubmit={handleMagicLink} className="space-y-4">
                    <div>
                      <Input
                        type="email"
                        placeholder="Email"
                        value={signInEmail}
                        onChange={(e) => setSignInEmail(e.target.value)}
                        className="bg-white/5 text-white border-white/10 focus:border-[#C0FD72] focus:ring-[#C0FD72]/20"
                        required
                      />
                    </div>
                    <Button
                      type="submit"
                      variant="lime"
                      className="w-full font-medium"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Sending...' : 'Email me a sign-in link'}
                    </Button>

                    <div className="pt-2 text-center">
                      <button
                        type="button"
                        onClick={() => setShowPasswordLogin(true)}
                        className="text-[#C0FD72] hover:text-[#a8e45a] font-medium text-sm underline underline-offset-4"
                      >
                        Sign in with password
                      </button>
                    </div>
                  </form>
                )}

                {needsVerification && (
                  <div className="mt-4 text-center">
                    <Button
                      variant="link"
                      onClick={handleResendConfirmation}
                      className="text-[#C0FD72] hover:text-[#a8e45a] font-medium"
                    >
                      Resend verification email
                    </Button>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default SignIn
